// AddEmployeeVehicle.tsx (or EmployeeVehiclePage.tsx)
import React, { useState, useEffect } from 'react';
import apiDefinitions from "../../../../core/api/apiDefinitions";
import {
  showSuccessAlert,
  showError,
  showItemExistsError,
  showDeleteConfirmation
} from "../../../../core/alert/sweetAlertConfig";
import { Button } from 'react-bootstrap';
import EmployeeVehicleFormModal from './EmployeeVehicleFormModal';
import EmployeeVehicleTable from './EmployeeVehicleTable';
import EmployeeVehicleViewModal from './EmployeeVehicleViewModal';
import {
  EmployeeVehicle,
  VehicleType,
  VehicleBrand,
  FormData,
  FormErrors
} from './types';

const initialFormData: FormData = {
  employeeNumber: "",
  vehicleTypeId: "",
  vehicleBrandId: "",
  vehicleColor: "",
  vehicleNumberPlate: "",
  status: "ACTIVE",
  limitPerDay: "", // added to match payload
};

const AddEmployeeVehicle: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({
    employeeNumber: "",
    vehicleTypeId: "",
    vehicleBrandId: "",
    vehicleColor: "",
    vehicleNumberPlate: "",
    status: "",
  });

  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [vehicleBrands, setVehicleBrands] = useState<VehicleBrand[]>([]);
  const [assignments, setAssignments] = useState<EmployeeVehicle[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;

  // Status filter state
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // View Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<EmployeeVehicle | null>(null);

  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchVehicleTypes(),
        fetchVehicleBrands()
      ]);
      fetchAssignments(currentPage, statusFilter);
    };
    loadData();
  }, [currentPage, statusFilter]);

  const fetchVehicleTypes = async () => {
    try {
      const res = await apiDefinitions.getAllVehicleTypes();
      if (res.data?.status === "S1000" && Array.isArray(res.data.data)) {
        setVehicleTypes(res.data.data);
      }
    } catch {
      showError("Failed to load vehicle types");
    }
  };

  const fetchVehicleBrands = async () => {
    try {
      const res = await apiDefinitions.getVehicleBrandModels("1", {});
      if (res.data?.status === "S1000" && res.data.data?.vehicleBrands) {
        setVehicleBrands(res.data.data.vehicleBrands);
      }
    } catch {
      showError("Failed to load vehicle brands");
    }
  };

  const fetchAssignments = async (page: number, status: string = 'ALL') => {
    setLoading(true);
    try {
      const payload = {
        filterType: status !== 'ALL' ? 'status' : null,
        filterValue: status !== 'ALL' ? status : '',
        date: null,
        from: null,
        to: null,
      };

      const res = await apiDefinitions.getAllEmployeeVehicles(page, pageSize, payload);

      if (res.data?.status === "S1000") {
        setAssignments(res.data.data || []);
        setTotalItems(res.data.total ?? res.data.data?.length ?? 0);
      }
    } catch (err) {
      showError("Failed to load assignments");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.employeeNumber.trim()) {
      newErrors.employeeNumber = "Employee is required";
    }
    if (!formData.vehicleTypeId) {
      newErrors.vehicleTypeId = "Vehicle type is required";
    }
    if (!formData.vehicleBrandId) {
      newErrors.vehicleBrandId = "Vehicle brand is required";
    }
    if (!formData.vehicleColor.trim()) {
      newErrors.vehicleColor = "Color is required";
    }
    if (!formData.vehicleNumberPlate.trim()) {
      newErrors.vehicleNumberPlate = "Number plate is required";
    } else if (!/^[A-Z]{2,3}-\d{4}$/i.test(formData.vehicleNumberPlate.trim())) {
      newErrors.vehicleNumberPlate = "Invalid format (e.g. ABC-1234)";
    }
    if (formData.limitPerDay === "" || isNaN(Number(formData.limitPerDay)) || Number(formData.limitPerDay) < 0) {
      newErrors.limitPerDay = "Please enter a valid non-negative number";
    }

    if (isEditMode && !formData.status) {
      newErrors.status = "Please select assignment status";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);

    const payload: any = {
      employeeNumber: parseInt(formData.employeeNumber.trim(), 10),
      vehicleTypeId: parseInt(formData.vehicleTypeId),
      vehicleBrandId: parseInt(formData.vehicleBrandId),
      vehicleColor: formData.vehicleColor.trim(),
      vehicleNumberPlate: formData.vehicleNumberPlate.trim().toUpperCase(),
      limitPerDay: formData.limitPerDay || "",
    };

    if (isEditMode && editId) {
      payload.vehicleId = editId;           // ← Uses "vehicleId" as per your update example
      payload.status = formData.status;     // ← Now correctly sent
    }

    try {
      const response = isEditMode
        ? await apiDefinitions.updateEmployeeVehicle(payload)
        : await apiDefinitions.addEmployeeVehicle(payload);

      if (response.data?.status === "S1000") {
        showSuccessAlert(isEditMode ? "Updated successfully" : "Assigned successfully");
        setShowModal(false);
        fetchAssignments(currentPage, statusFilter);
        resetForm();
      } else if (response.data?.statusDescription?.toLowerCase().includes("already")) {
        showItemExistsError("Assign already exists");
      } else {
        showError(response.data?.statusDescription || "Operation failed");
      }
    } catch (err: any) {
      showError(err.response?.data?.statusDescription || "Server error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id: number) => {
    showDeleteConfirmation(async () => {
      try {
        const res = await apiDefinitions.deleteEmployeeVehicle(id);
        if (res.data?.status === "S1000") {
          showSuccessAlert("Deleted successfully");
          fetchAssignments(currentPage, statusFilter);
        } else {
          showError("Delete failed");
        }
      } catch {
        showError("Error while deleting");
      }
    });
  };

  const handleEdit = (item: EmployeeVehicle) => {
    setIsEditMode(true);
    setEditId(item.id);
    setFormData({
      employeeNumber: item.employee.employeeNumber.toString(),
      vehicleTypeId: item.vehicleTypeId.toString(),
      vehicleBrandId: item.vehicleBrandId.toString(),
      vehicleColor: item.vehicleColor,
      vehicleNumberPlate: item.vehicleNumberPlate,
      status: item.status,
      limitPerDay: item.limitPerDay?.toString() || "", // support if backend returns number
    });
    setShowModal(true);
  };

  const handleView = (item: EmployeeVehicle) => {
    setSelectedAssignment(item);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedAssignment(null);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(0);
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setErrors({
      employeeNumber: "",
      vehicleTypeId: "",
      vehicleBrandId: "",
      vehicleColor: "",
      vehicleNumberPlate: "",
      status: "",
    });
    setIsEditMode(false);
    setEditId(null);
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h4 className="page-title">Employee Vehicle Assignments</h4>
            </div>
            <div className="col-auto">
              <Button
                variant="success"
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
              >
                + New Assign
              </Button>
            </div>
          </div>
        </div>

        <EmployeeVehicleViewModal
          show={showViewModal}
          onHide={handleCloseViewModal}
          assignment={selectedAssignment}
        />

        <EmployeeVehicleTable
          assignments={assignments}
          loading={loading}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onStatusFilterChange={handleStatusFilterChange}
        />

        <EmployeeVehicleFormModal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            resetForm();
          }}
          formData={formData}
          errors={errors}
          onChange={handleInputChange}
          onSubmit={handleSubmit}
          submitting={submitting}
          isEditMode={isEditMode}
          vehicleTypes={vehicleTypes}
          vehicleBrands={vehicleBrands}
        />
      </div>
    </div>
  );
};

export default AddEmployeeVehicle;